import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Box, Paper, TableContainer, LinearProgress, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from "react-router";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import SearchField from '../../../components/search/Search';
import { useProductionStatusMonthly } from './useProductionStatusMonthly';
import ErrorNotification from '../../../components/ErrorNotification/ErrorNotification';
import api from "../../../services/api";
import { useNotifier } from "muinotify";
import { exportToExcel } from '../../../components/export/exportToExcel';
import { exportToPdf } from '../../../components/export/exportToPdf';
import { exportToCsv } from '../../../components/export/exportToCsv';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';


const ProductionStatusMonthly = () => {
  const { notifier } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const { category_item, isLoading, error, mutate } = useProductionStatusMonthly(searchQuery);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(null);

  const URL = `production-category-item`;
  const NAV_PATH = `category_item`;

  const getRandom = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  function createData(name) {
    const values = Array.from({ length: 12 }, () => getRandom(800, 2500));
    return { name, values };
  }

  const rows = [
    createData('ΑΡΧΙΣΥΝΤΑΚΤΗΣ'),
    createData('ΒΡΑΒΕΙΑ ΠΑΙΚΤΩΝ (ΤΗΛΕΠΑΙΧΝΙΔΙΑ)'),
    createData('ΔΗΜΟΣΙΟΓΡΑΦΟΣ'),
    createData('ΔΙΕΥΘΥΝΤΗΣ ΠΑΡΑΓΩΓΗΣ'),
    createData('ΔΙΕΥΘΥΝΤΗΣ ΦΩΤΟΓΡΑΦΙΑΣ'),
    createData('ΕΙΚΟΝΟΛΗΠΤΗΣ'),
    createData('ΕΝΔΥΜΑΤΟΛΟΓΟΣ'),
    createData('ΕΠΙΣΤΗΜΟΝΙΚΟΣ ΣΥΝΕΡΓΑΤΗΣ'),
    createData('ΗΘΟΠΟΙΟΙ'),
    createData('ΗΘΟΠΟΙΟΙ Β΄'),
    createData('ΗΧΟΛΗΠΤΗΣ'),
    createData('ΚΑΛΛΙΤΕΧΝΙΚΟΣ ΔΙΕΥΘΥΝΤΗΣ'),
    createData('ΜΑΚΕΝΙΣΤΑΣ'),
    createData('ΜΕΤΑΦΡΑΣΤΗΣ'),
    createData('ΜΟΝΤΕΡ'),
    createData('ΜΟΥΣΙΚΗ ΕΠΙΜΕΛΕΙΑ'),
    createData('ΜΟΥΣΙΚΟΙ'),
    createData('ΜΟΥΣΙΚΟΣ ΠΑΡΑΓΩΓΟΣ'),
    createData('ΠΑΡΟΥΣΙΑΣΤΗΣ Α'),
    createData('ΠΑΡΟΥΣΙΑΣΤΗΣ Β'),
    createData('ΠΝΕΥΜΑΤΙΚΑ ΔΙΚΑΙΩΜΑΤΑ'),
    createData('ΣΕΝΑΡΙΟΓΡΑΦΟΣ'),
    createData('ΣΚΗΝΟΓΡΑΦΟΣ'),
    createData('ΣΚΗΝΟΘΕΤΗΣ'),
    createData('ΣΥΝΘΕΤΗΣ ΠΡΩΤΟΤΥΠΗΣ ΜΟΥΣΙΚΗΣ'),
    createData('ΤΡΑΓΟΥΔΙΣΤΕΣ'),
    createData('ΥΛΙΚΟ ΑΡΧΕΙΟΥ'),
    createData('ΥΠΕΥΘΥΝΟΣ ΕΡΓΟΥ (Project Manager)'),
    createData('ΦΩΤΟΓΡΑΦΟΣ'),
  ];


  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const calculateSums = () => {
    const sums = Array(12).fill(0);

    rows.forEach(row => {
      row.values.forEach((value, index) => {
        sums[index] += value;
      });
    });

    return sums;
  };

  const sums = calculateSums();

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  function getRowId(row) {
    return row.id;
  }

  const handleClear = () => {
    setSearchQuery("");
    mutate(); // Refetch data after clearing search
  };

  let navigate = useNavigate();

  const handlePanelClose = () => {
    setSelectedCategoryItem(null);
  };

  const handleView = (category_item) => {
    setSelectedCategoryItem(category_item);
  };

  const handleEdit = (id) => {
    navigate(`/${NAV_PATH}/update/${id}`);
  };

  const handleAdd = () => {
    navigate(`/${NAV_PATH}/create/`);
  };

  const handleDelete = useCallback((id) => {
    confirmAlert({
      title: 'Confirm to delete',
      message: 'Are you sure you want to delete this category_item?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              await api.delete(`/${URL}/${id}`);
              notifier("Production deleted successfully.", { variant: "success" });
              await mutate();
            } catch (err) {
              notifier("Failed to delete production <Error: " + err.message + ">", { variant: "error" });
            }
          }
        },
        {
          label: 'No',
        }
      ]
    });
  }, [mutate, notifier]);


  const columnsToExtract = ['clientName', 'clientName', 'contactPerson', 'clientAddress', 'clientCity', 'clientPhone', 'clientEmail', 'clientNotes'];

  const handleExportToExcel = () => {
    exportToExcel(category_item || [], columns, columnsToExtract, 'clients-list', 'Clients List');
  };

  const handleExportToPdf = () => {
    exportToPdf(category_item || [], columns, columnsToExtract, 'clients-list');
  };

  const handleExportToCsv = () => {
    exportToCsv(category_item || [], columns, columnsToExtract, 'clients-list');
  };

  const columns = [];

  return (
    <Box m="20px" >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Category Items" module="Productions" subtitle="List of category items" />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={selectedCategoryItem ? 8 : 12}>

          <FormListToolbar
            handleAdd={handleAdd}
            handleExportToExcel={handleExportToExcel}
            handleExportToPdf={handleExportToPdf}
            handleExportToCsv={handleExportToCsv}
          />
          <Box
            m="8px 0 0 0"
            height="80vh"
          >
            <Paper elevation={2} style={{ padding: '1rem', height: '100%', width: '100%' }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'black' }}>
                Year: 2024
              </Typography>
              {isLoading && <LinearProgress />}
              {error && <ErrorNotification error={error} />}
              <TableContainer style={{ maxHeight: '98%', overflowY: 'auto' }} >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>Episode</TableCell>
                      {/* Array of 12 months */}
                      {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, index) => (
                        <TableCell sx={{ fontWeight: 'bold', color: 'black' }} key={index}>{month}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        <TableCell>{row.name}</TableCell>
                        {row.values.map((value, valueIndex) => (
                          <TableCell key={valueIndex}>{formatCurrency(value)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {/* Add sums row */}
                    <TableRow>
                      <TableCell><strong>Monthly Total</strong></TableCell>
                      {sums.map((sum, index) => (
                        <TableCell key={index}><strong>{formatCurrency(sum)}</strong></TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </Grid>
        {/* {selectedCategoryItem && (
          <Grid item xs={4}>
            <ProductionCategoryItemDetails
              category_item={selectedCategoryItem}
              onClose={handlePanelClose} />
          </Grid>
        )} */}
      </Grid>
    </Box>

  );
};

export default ProductionStatusMonthly;